import React from 'react';
import { Helmet } from 'react-helmet-async';
import { BRAND } from '../config/brand';
import { normalizeBrandTitle } from '../utils/brandTitle';

const SITE_URL = BRAND.url;

interface SEOProps {
  title?: string;
  description?: string;
  path?: string;
  image?: string;
  keywords?: string;
  robots?: string;
}

const buildCanonical = (path: string) => {
  if (/^https?:\/\//.test(path)) return path;
  if (!path || path === '/') return `${SITE_URL}/`;
  return `${SITE_URL}${path.startsWith('/') ? path : `/${path}`}`;
};

const dedupeHeadTags = () => {
  [
    'meta[name="description"]',
    'meta[name="keywords"]',
    'meta[name="robots"]',
    'link[rel="canonical"]',
    'meta[property="og:title"]',
    'meta[property="og:description"]',
    'meta[property="og:url"]',
    'meta[property="og:image"]',
    'meta[name="twitter:title"]',
    'meta[name="twitter:description"]',
    'meta[name="twitter:image"]',
  ].forEach((selector) => {
    const nodes = Array.from(document.head.querySelectorAll(selector));
    nodes.slice(0, -1).forEach((node) => node.remove());
  });
};

const SEO: React.FC<SEOProps> = ({
  title = 'Kundlistar Studio - KP, Nadi, BNN & Vedic Astrology Software',
  description = 'Kundlistar Studio is an astrology workspace for Vedic astrology, KP astrology, Nadi astrology, BNN, Jaimini, Numerology and client management.',
  path = '/',
  image = '/og-image.jpg',
  keywords = 'Kundlistar, Kundlistar astrology, Kundlistar app, KP astrology software, Nadi astrology software, BNN astrology, Vedic astrology software, Kundli software',
  robots = 'index,follow',
}) => {
  const canonicalUrl = buildCanonical(path);
  const imageUrl = image.startsWith('http') ? image : `${SITE_URL}${image}`;
  const normalizedTitle = normalizeBrandTitle(title);

  React.useEffect(() => {
    dedupeHeadTags();
  }, [canonicalUrl, description, imageUrl, keywords, normalizedTitle, robots]);

  return (
    <Helmet>
      <title>{normalizedTitle}</title>
      <meta name="description" content={description} />
      <meta name="keywords" content={keywords} />
      <meta name="robots" content={robots} />
      <link rel="canonical" href={canonicalUrl} />

      <meta property="og:type" content="website" />
      <meta property="og:site_name" content={BRAND.name} />
      <meta property="og:title" content={normalizedTitle} />
      <meta property="og:description" content={description} />
      <meta property="og:url" content={canonicalUrl} />
      <meta property="og:image" content={imageUrl} />

      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={normalizedTitle} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={imageUrl} />

      <meta name="application-name" content={BRAND.name} />
      <meta name="apple-mobile-web-app-title" content={BRAND.name} />
    </Helmet>
  );
};

export default SEO;
